import { Family } from '@jbhi-fi/apis/src/shopify/types/OnlineProduct';
import { MarketplaceSellerCalloutProps } from '../../Marketplace/MarketplaceSellerCallout/MarketplaceSellerCallout';
import { OnlineProduct } from '@jbhi-fi/apis/src/shopify/types/OnlineProduct';
import { AlgoliaHitTransformer } from '../../Algolia/transformers/AlgoliaHitTransformer';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { MarketplaceSellerMetaObject } from '../../Marketplace/MarketplaceSellerCallout/types';
import { ShopifyProduct } from '../types';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';

const DEFAULT_VARIANT_TITLE = 'Default Title';

type ShopifyProductTransformerBaseProps = {
  product: ShopifyProduct;
  onlineProduct?: OnlineProduct;
  marketplaceSellers?: Array<MarketplaceSellerMetaObject>;
};

export type ShopifyGqlProduct = {
  handle: string;
  id: string;
  metafields: {
    onlineProduct: OnlineProduct;
  };
};

export abstract class ShopifyProductTransformer {
  static isMarketplaceProduct = ({ product }: ShopifyProductTransformerBaseProps): boolean => {
    return product?.variants?.some(variant => variant.title !== DEFAULT_VARIANT_TITLE);
  };

  static toMarketplaceSellerCalloutProps = ({
    product,
    marketplaceSellers,
  }: ShopifyProductTransformerBaseProps):
    | Pick<MarketplaceSellerCalloutProps, 'sellerName' | 'sellerHref'>
    | undefined => {
    const seller = marketplaceSellers?.[0];

    if (!this.isMarketplaceProduct({ product, marketplaceSellers }) || !seller) {
      return undefined;
    }

    return {
      sellerName: seller?.name ?? '',
      sellerHref: seller?.url_handle ? `/collections/${seller?.url_handle}` : undefined,
    };
  };

  static toMarketplaceSeller = ({
    product,
    marketplaceSellers,
    onlineProduct,
  }: ShopifyProductTransformerBaseProps): ReturnType<typeof AlgoliaHitTransformer.toMarketplaceSeller> => {
    const marketplaceSeller = marketplaceSellers?.[0];

    if (!this.isMarketplaceProduct({ product, marketplaceSellers }) || !marketplaceSeller) {
      return undefined;
    }
    const tieredDetails = onlineProduct?.TieredDetails?.filter(
      tieredDetail => `${tieredDetail.Seller.Id}` === `${marketplaceSeller?.seller_id}`
    );

    return {
      name: marketplaceSeller.name,
      id: marketplaceSeller.seller_id,
      tieredIds: tieredDetails?.map(tieredDetail => tieredDetail.Id) ?? [],
      productCondition: {
        code: tieredDetails?.[0]?.Condition?.Code,
        label: tieredDetails?.[0]?.Condition?.Label ?? '',
      },
      isDigitalInvoiceProvided: marketplaceSeller.is_digital_invoice_provided ?? false,
    };
  };

  static toOnlineHit = (shopifyGqlProduct: ShopifyGqlProduct, shopifyFamily?: Family): AlgoliaProductHit => {
    const shopifyProduct = shopifyGqlProduct.metafields.onlineProduct;
    const { Display, Availability, Product, Price, ProductTags } = shopifyProduct;
    const specificationDetails = Display.SpecificationDetails ?? [];
    const facets: { [key: string]: Array<string> | Array<number> } = specificationDetails.reduce((acc, spec) => {
      acc[spec.Name] = spec.Values;
      return acc;
    }, {} as { [key: string]: Array<string> | Array<number> });
    const productTags = {
      allCalculatedProductTags: ProductTags?.AllCalculatedProductTags ?? [],
      bannerTag: ProductTags?.BannerTag ?? {},
      promoTags:
        ProductTags?.PromoTags?.map(tag => ({
          label: tag.Label,
          colour: tag.Colour,
          backgroundColour: tag.BackgroundColour,
        })) ?? [],
    };
    const promoTags =
      ProductTags?.PromoTags?.map(tag => ({
        background_color: tag.BackgroundColour,
        color: tag.Colour,
        label: tag.Label,
      })) ?? [];
    const displayImages = Display.Images.map(image => ({
      ResourceId: '',
      Timestamp: '',
      ImageIndex: image.ImageIndex,
      ImageCategory: image.ImageCategory,
      Extension: '',
      ImageHash: '',
      ImageName: '',
      ImageUrl: image.ImageUrl,
    }));

    const supplierProductDetails = Product.SupplierProductDetails.map(({ SupplierNumber }) => ({
      supplierNumber: Number(SupplierNumber),
    }));

    const productGroup = shopifyProduct.ProductGroup
      ? {
          id: Number(shopifyProduct.ProductGroup.Id),
          description: shopifyProduct.ProductGroup.Description ?? '',
          timestamp: '',
        }
      : undefined;

    const tieredDetails = shopifyProduct.TieredDetails
      ? shopifyProduct.TieredDetails.map(tieredDetail => ({
          id: tieredDetail.Id,
          ranking: tieredDetail.Ranking,
          seller: {
            id: tieredDetail.Seller.Id.toString(),
            name: tieredDetail.Seller.Name,
          },
          availability: {
            ...toCamelCase(Availability),
          },
          pricing: {
            ...toCamelCase(Price),
          },
          condition: {
            code: tieredDetail?.Condition?.Code ?? undefined,
            label: tieredDetail?.Condition?.Label ?? '',
          },
        }))
      : [];

    return {
      sku: shopifyProduct.Id ?? '',
      skus: shopifyFamily?.Skus ?? [],
      options: shopifyFamily?.Options.map(option => ({
        SpecificationName: option.Name,
        SpecRender: option.SpecRender,
        Values: option.Children,
      })),
      children: shopifyFamily?.Children ?? [],
      is_family: !!shopifyFamily?.Id,
      online_product_timestamp: shopifyProduct.Timestamp ?? '',
      title: Display.OnlineDescription ?? '',
      primary_title: Display.OnlineDescription ?? '',
      secondary_title: Display.OnlineDescription ?? '',
      category_id: shopifyProduct.Category.Id,
      category: shopifyProduct.Category.Name,
      category_hierarchy: Display.CategoryHierarchy ?? [],
      release_date: Product.ReleaseDate,
      taxable: Price.DisplayPriceTax > 0,
      price: Price.DisplayPriceInc,
      weight: Product.PackagedDimensions.Weight ?? 0,
      all_calculated_product_tags: ProductTags?.AllCalculatedProductTags,
      compare_at_price: Price.WasPrice && Price.DisplayWasPrice !== null ? Price.WasPrice.toString() : '',
      button: shopifyProduct.Button,
      preamble: Display.Preamble,
      brand_image_url: Display.BrandImageUrl,
      template_name: Display.TemplateName,
      showPerksPrice: shopifyProduct.ShowPerksPrice,

      facets: facets,

      banner_tags: ProductTags?.BannerTag ?? {},
      promo_tags: promoTags,
      showWasPrice: Price.DisplayWasPrice,
      barcode: Product.Barcode ?? '',
      priceBannerTag: ProductTags?.PriceBannerTag
        ? {
            backgroundColor: ProductTags?.PriceBannerTag.BackgroundColour,
            label: ProductTags.PriceBannerTag.Label,
            color: ProductTags.PriceBannerTag.Colour,
          }
        : undefined,
      product_image: Display.Images[0].ImageUrl ?? '',
      product: {
        id: shopifyProduct.Id ?? '',
        freightPrice: shopifyProduct.Product?.FreightPrice ?? '',
        ean13: Product.EAN13 ?? '',
        description: Display.OnlineDescription ?? '',
        model: Product.Model ?? '',
        brand: Product.Brand ?? '',
        departmentCode: Number(Product.Department?.Id) ?? 0,
        format: Display.Format ?? '',
        supplierProductDetails: supplierProductDetails ?? [],
        packagedDimensions: {
          height: Number(Product.PackagedDimensions.Height) ?? 0,
          width: Number(Product.PackagedDimensions.Width) ?? 0,
          depth: Number(Product.PackagedDimensions.Depth) ?? 0,
          weight: Number(Product.PackagedDimensions.Weight) ?? 0,
        },
        releaseDate: Product.ReleaseDate ?? '',
        releaseDateText: Product.ReleaseDateText ?? '',
        limitPerOrder: Product.LimitPerOrder ?? '',
        limitedRunPromotionItems: Product.LimitedRunPromotionItems ?? [],
        campaigns: Product.Campaigns ?? [],
        hasOnlineCategory: Product.HasOnlineCategory ?? false,
        showReviews: shopifyProduct.Display.ShowReviews ?? false,
        averageProductReviewRating: Display.AverageProductReviewRating ?? 0,
        numberOfProductReviews: Display.NumberOfProductReviews ?? 0,
        productGroupName: Product.ProductGroup?.Description ?? '',
        departmentName: shopifyProduct.Department.Description ?? '',
        productFlags: Product.Flags.map(flag => ({
          Name: flag.Name,
          Id: Number(flag.Id),
        })),
        seller: Product.Seller
          ? {
              id: Product.Seller.Id.toString(),
              name: Product.Seller.Name,
            }
          : undefined,
      },
      productGroup,
      department: shopifyProduct.Department ?? {},
      productTags,
      deliveryTag: shopifyProduct.DeliveryTag,
      display: {
        artist: Display.Artist ?? '',
        tracklist: Display.Tracklist ?? [],
        actors: Display.Actors?.toString() ?? '',
        directors: Display.Directors?.toString() ?? '',
        images: displayImages,
        keyFeatures: Display.KeyFeatures ?? [],
        extendedDescription: Display.ExtendedDescription,
        categoryHierarchy: Display.CategoryHierarchy ?? [],
        graphicAttributes: Display.GraphicAttributes,
      },
      availability: {
        ...toCamelCase(Availability),
      },
      pricing: {
        ...toCamelCase(Price),
      },
      relatedProductSkus: Display.RelatedProductIds ?? [],
      perksPricing: {
        ...toCamelCase(Price),
        promoNamesApplied: Price.PerksPricing?.PromoNamesApplied ?? [],
        wasPrice: Price.PerksPricing?.WasPrice ?? 0,
      },
      google_tag_manager: toCamelCase(ProductTags?.GoogleTagManager),
      tieredDetails: tieredDetails,
      onPromotion: Product.OnPromotion,
      weighted_average_rating: Display.WeightedAverageRating,
      product_published: true,
      product_type: shopifyProduct.ProductType ?? undefined,
      vendor: shopifyProduct.Vendor ?? undefined,
      product_id: Number(shopifyProduct.Id) ?? 0,
      updated_at: shopifyProduct.Timestamp ?? '',
      tags: Availability.OverallStatus ?? '',
      overallStatus: Availability.OverallStatus ?? '',
      timestamp: shopifyProduct.Timestamp ?? '',
      availabilityStatement: Availability.AvailabilityStatement ?? '',
      shopify_timestamp: shopifyProduct.Timestamp ?? '',
      objectID: shopifyFamily?.Id ?? '',
      keepSplit: shopifyFamily?.KeepSlit ?? false,
      global_variant_id: shopifyGqlProduct.id,
      variant_id: parseInt(shopifyGqlProduct.id.match(/\d/g)?.[0] ?? '0'),
      handle: shopifyGqlProduct.handle,
      schemaVersion: 0,
      bnplExcluded: false,
      onlineProductCategory: toCamelCase(shopifyProduct.Category),
    };
  };
}
